import React, { useState, useEffect } from 'react';
import {
    Card,
    CardContent,
    Typography,
    Box,
    Chip,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Alert,
    CircularProgress,
    Button,
    IconButton,
    Tooltip,
    useMediaQuery,
    useTheme,
} from '@mui/material';
import {
    Schedule,
    CheckCircle,
    Error,
    Warning,
    Refresh,
    AccessTime,
    Update,
    History,
} from '@mui/icons-material';
import { useDataProvider, useTranslate, useNotify, useLocaleState } from 'react-admin';
import { formatDistanceToNow } from 'date-fns';

interface CronStatus {
    cronJob: {
        name: string;
        schedule: string;
        description: string | { en: string; 'zh-TW': string };
        timezone: string;
        environment: string;
    };
    status: {
        lastRun: {
            time: string;
            status: string;
            details: {
                applied: number;
                errors: number;
            };
        } | null;
        nextRun: string;
        upcomingChangesCount: number;
    };
    upcomingChanges: Array<{
        id: number;
        templateId: string;
        templateName: string;
        effectiveDate: string;
        reason: string | null;
        createdBy: string;
        hoursUntilEffective: number;
    }>;
    recentExecutions: Array<{
        time: string;
        status: string;
        details: {
            applied: number;
            errors: number;
        };
    }>;
}

export const CronMonitor = () => {
    const [cronStatus, setCronStatus] = useState<CronStatus | null>(null);
    const [loading, setLoading] = useState(true);
    const [refreshing, setRefreshing] = useState(false);
    const [triggering, setTriggering] = useState(false);
    const dataProvider = useDataProvider();
    const translate = useTranslate();
    const notify = useNotify();
    const [locale] = useLocaleState();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const fetchCronStatus = async (showRefreshing = false) => {
        if (showRefreshing) setRefreshing(true);
        try {
            const response = await dataProvider.getOne('backend/cron/status', { id: 'current' });
            setCronStatus(response.data as CronStatus);
        } catch (error) {
            notify(translate('cronMonitor.messages.failedToLoad'), { type: 'error' });
            console.error('Error fetching cron status:', error);
        } finally {
            setLoading(false);
            setRefreshing(false);
        }
    };

    useEffect(() => {
        fetchCronStatus();
        const interval = setInterval(() => fetchCronStatus(false), 30000);
        return () => clearInterval(interval);
    }, []);

    const handleTriggerCron = async () => {
        setTriggering(true);
        try {
            const response = await dataProvider.create('backend/cron/trigger', { data: {} });
            notify(translate('cronMonitor.messages.cronTriggeredSuccess'), { type: 'success' });
            setTimeout(() => fetchCronStatus(true), 2000);
        } catch (error) {
            notify(translate('cronMonitor.messages.cronTriggerFailed'), { type: 'error' });
            console.error('Error triggering cron:', error);
        } finally {
            setTriggering(false);
        }
    };

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'success':
                return 'success';
            case 'partial_success':
                return 'warning';
            case 'failed':
                return 'error';
            default:
                return 'default';
        }
    };

    const getStatusIcon = (status: string) => {
        switch (status) {
            case 'success':
                return <CheckCircle />;
            case 'partial_success':
                return <Warning />;
            case 'failed':
                return <Error />;
            default:
                return <Schedule />;
        }
    };

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!cronStatus) {
        return (
            <Alert severity="error">
                {translate('cronMonitor.messages.failedToLoad')}
            </Alert>
        );
    }

    const isOverdue = (effectiveDate: string) => {
        return new Date(effectiveDate) < new Date();
    };

    const getLocalizedDescription = (description: string | { en: string; 'zh-TW': string }) => {
        if (typeof description === 'string') {
            return description;
        }
        return description[locale as 'en' | 'zh-TW'] || description.en;
    };

    return (
        <Box sx={{ maxWidth: 1200, margin: '0 auto', p: { xs: 0, sm: 3 } }}>
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', sm: 'center' },
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 1.5, sm: 2 },
                mb: { xs: 2, sm: 3 },
            }}>
                <Typography sx={{ fontWeight: 600, fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
                    {translate('cronMonitor.title')}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', width: { xs: '100%', sm: 'auto' } }}>
                    {cronStatus.cronJob.environment === 'development' && (
                        <Button
                            variant="contained"
                            color="warning"
                            startIcon={triggering ? <CircularProgress size={20} /> : <Schedule />}
                            onClick={handleTriggerCron}
                            disabled={triggering}
                            size={isMobile ? 'small' : 'medium'}
                            sx={{ flex: { xs: 1, sm: 'none' }, textTransform: 'none' }}
                        >
                            {translate('cronMonitor.buttons.runNow')}
                        </Button>
                    )}
                    <Button
                        variant="outlined"
                        startIcon={refreshing ? <CircularProgress size={20} /> : <Refresh />}
                        onClick={() => fetchCronStatus(true)}
                        disabled={refreshing}
                        size={isMobile ? 'small' : 'medium'}
                        sx={{ flex: { xs: 1, sm: 'none' }, textTransform: 'none' }}
                    >
                        {translate('cronMonitor.buttons.refresh')}
                    </Button>
                </Box>
            </Box>

            {/* Cron Job Info */}
            <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1.5, sm: 2 }, flexWrap: 'wrap', gap: 1 }}>
                        <Schedule sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' }, wordBreak: 'break-word' }}>
                            {cronStatus.cronJob.name}
                        </Typography>
                        <Chip
                            label={cronStatus.cronJob.environment}
                            size="small"
                            color={cronStatus.cronJob.environment === 'production' ? 'error' : 'info'}
                        />
                    </Box>

                    <Box sx={{ display: 'grid', gap: { xs: 1.5, sm: 2 }, gridTemplateColumns: { xs: '1fr', sm: 'repeat(auto-fit, minmax(220px, 1fr))' } }}>
                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                {translate('cronMonitor.fields.schedule')}
                            </Typography>
                            <Typography variant="body1" sx={{ fontFamily: 'monospace', wordBreak: 'break-all' }}>
                                {cronStatus.cronJob.schedule}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-word' }}>
                                {getLocalizedDescription(cronStatus.cronJob.description)}
                            </Typography>
                        </Box>

                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                {translate('cronMonitor.fields.nextRun')}
                            </Typography>
                            <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                                {new Date(cronStatus.status.nextRun).toLocaleString()}
                            </Typography>
                            <Typography variant="caption" color="primary.main">
                                {formatDistanceToNow(new Date(cronStatus.status.nextRun), { addSuffix: true })}
                            </Typography>
                        </Box>

                        <Box>
                            <Typography variant="caption" color="text.secondary">
                                {translate('cronMonitor.fields.lastRun')}
                            </Typography>
                            {cronStatus.status.lastRun ? (
                                <>
                                    <Typography variant="body1" sx={{ wordBreak: 'break-word' }}>
                                        {new Date(cronStatus.status.lastRun.time).toLocaleString()}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                                        <Chip
                                            icon={getStatusIcon(cronStatus.status.lastRun.status)}
                                            label={translate('cronMonitor.messages.appliedWithErrors', {
                                                applied: cronStatus.status.lastRun.details.applied,
                                                errors: cronStatus.status.lastRun.details.errors
                                            })}
                                            size="small"
                                            color={getStatusColor(cronStatus.status.lastRun.status)}
                                        />
                                    </Box>
                                </>
                            ) : (
                                <Typography variant="body1" color="text.secondary">
                                    {translate('cronMonitor.messages.noExecutionsYet')}
                                </Typography>
                            )}
                        </Box>
                    </Box>
                </CardContent>
            </Card>

            {/* Upcoming Changes */}
            <Card sx={{ mb: { xs: 2, sm: 3 }, overflow: 'hidden' }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1.5, sm: 2 }, flexWrap: 'wrap', gap: 1 }}>
                        <Update sx={{ mr: 1, color: 'warning.main' }} />
                        <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                            {translate('cronMonitor.sections.upcomingChanges')}
                        </Typography>
                        <Chip
                            label={cronStatus.status.upcomingChangesCount}
                            size="small"
                            color="warning"
                        />
                    </Box>

                    {cronStatus.upcomingChanges.length > 0 ? (
                        isMobile ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {cronStatus.upcomingChanges.map((change) => (
                                    <Box key={change.id} sx={{ p: 1.5, border: '1px solid #E0E0E0', borderRadius: 2, overflow: 'hidden' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1, mb: 1 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 600, wordBreak: 'break-word', flex: 1, minWidth: 0 }}>
                                                {change.templateName}
                                            </Typography>
                                            {change.hoursUntilEffective <= 0 ? (
                                                <Chip
                                                    label={translate('cronMonitor.status.readyToApply')}
                                                    size="small"
                                                    color="warning"
                                                    icon={<AccessTime />}
                                                    sx={{ flexShrink: 0 }}
                                                />
                                            ) : isOverdue(change.effectiveDate) ? (
                                                <Chip
                                                    label={translate('cronMonitor.status.overdue')}
                                                    size="small"
                                                    color="error"
                                                    sx={{ flexShrink: 0 }}
                                                />
                                            ) : null}
                                        </Box>
                                        <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1 }}>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    {translate('cronMonitor.fields.effectiveDate')}
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                                    {new Date(change.effectiveDate).toLocaleString()}
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    {translate('cronMonitor.fields.status')}
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontSize: '0.75rem' }}>
                                                    {change.hoursUntilEffective > 0
                                                        ? translate('cronMonitor.messages.inHours', { hours: change.hoursUntilEffective })
                                                        : translate('cronMonitor.status.readyToApply')}
                                                </Typography>
                                            </Box>
                                            {change.reason && (
                                                <Box>
                                                    <Typography variant="caption" color="text.secondary">
                                                        {translate('cronMonitor.fields.reason')}
                                                    </Typography>
                                                    <Typography variant="body2" sx={{ fontSize: '0.75rem', wordBreak: 'break-word' }}>
                                                        {change.reason}
                                                    </Typography>
                                                </Box>
                                            )}
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    {translate('cronMonitor.fields.createdBy')}
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontSize: '0.75rem', wordBreak: 'break-word' }}>
                                                    {change.createdBy}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{translate('cronMonitor.fields.template')}</TableCell>
                                            <TableCell>{translate('cronMonitor.fields.effectiveDate')}</TableCell>
                                            <TableCell>{translate('cronMonitor.fields.status')}</TableCell>
                                            <TableCell>{translate('cronMonitor.fields.reason')}</TableCell>
                                            <TableCell>{translate('cronMonitor.fields.createdBy')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {cronStatus.upcomingChanges.map((change) => (
                                            <TableRow key={change.id}>
                                                <TableCell>{change.templateName}</TableCell>
                                                <TableCell>
                                                    <Box>
                                                        <Typography variant="body2">
                                                            {new Date(change.effectiveDate).toLocaleString()}
                                                        </Typography>
                                                        {isOverdue(change.effectiveDate) && (
                                                            <Chip
                                                                label={translate('cronMonitor.status.overdue')}
                                                                size="small"
                                                                color="error"
                                                                sx={{ mt: 0.5 }}
                                                            />
                                                        )}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    {change.hoursUntilEffective <= 0 ? (
                                                        <Chip
                                                            label={translate('cronMonitor.status.readyToApply')}
                                                            size="small"
                                                            color="warning"
                                                            icon={<AccessTime />}
                                                        />
                                                    ) : (
                                                        <Typography variant="caption">
                                                            {translate('cronMonitor.messages.inHours', { hours: change.hoursUntilEffective })}
                                                        </Typography>
                                                    )}
                                                </TableCell>
                                                <TableCell>{change.reason || '-'}</TableCell>
                                                <TableCell>{change.createdBy}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )
                    ) : (
                        <Alert severity="info">
                            {translate('cronMonitor.messages.noUpcomingChanges')}
                        </Alert>
                    )}
                </CardContent>
            </Card>

            {/* Recent Executions */}
            <Card sx={{ overflow: 'hidden' }}>
                <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: { xs: 1.5, sm: 2 }, flexWrap: 'wrap', gap: 1 }}>
                        <History sx={{ mr: 1, color: 'info.main' }} />
                        <Typography variant="h6" sx={{ fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                            {translate('cronMonitor.sections.recentExecutions')}
                        </Typography>
                    </Box>

                    {cronStatus.recentExecutions.length > 0 ? (
                        isMobile ? (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                                {cronStatus.recentExecutions.map((execution, index) => (
                                    <Box key={index} sx={{ p: 1.5, border: '1px solid #E0E0E0', borderRadius: 2, overflow: 'hidden' }}>
                                        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, mb: 1 }}>
                                            <Box sx={{ minWidth: 0 }}>
                                                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: '0.8rem' }}>
                                                    {new Date(execution.time).toLocaleString()}
                                                </Typography>
                                                <Typography variant="caption" color="text.secondary">
                                                    {formatDistanceToNow(new Date(execution.time), { addSuffix: true })}
                                                </Typography>
                                            </Box>
                                            <Chip
                                                icon={getStatusIcon(execution.status)}
                                                label={execution.status}
                                                size="small"
                                                color={getStatusColor(execution.status)}
                                                sx={{ flexShrink: 0 }}
                                            />
                                        </Box>
                                        <Box sx={{ display: 'flex', gap: 2 }}>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    {translate('cronMonitor.fields.applied')}
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                    {execution.details.applied}
                                                </Typography>
                                            </Box>
                                            <Box>
                                                <Typography variant="caption" color="text.secondary">
                                                    {translate('cronMonitor.fields.errors')}
                                                </Typography>
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: execution.details.errors > 0 ? 'error.main' : 'text.primary' }}>
                                                    {execution.details.errors}
                                                </Typography>
                                            </Box>
                                        </Box>
                                    </Box>
                                ))}
                            </Box>
                        ) : (
                            <TableContainer component={Paper} variant="outlined">
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>{translate('cronMonitor.fields.executionTime')}</TableCell>
                                            <TableCell>{translate('cronMonitor.fields.status')}</TableCell>
                                            <TableCell>{translate('cronMonitor.fields.applied')}</TableCell>
                                            <TableCell>{translate('cronMonitor.fields.errors')}</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {cronStatus.recentExecutions.map((execution, index) => (
                                            <TableRow key={index}>
                                                <TableCell>
                                                    <Box>
                                                        <Typography variant="body2">
                                                            {new Date(execution.time).toLocaleString()}
                                                        </Typography>
                                                        <Typography variant="caption" color="text.secondary">
                                                            {formatDistanceToNow(new Date(execution.time), { addSuffix: true })}
                                                        </Typography>
                                                    </Box>
                                                </TableCell>
                                                <TableCell>
                                                    <Chip
                                                        icon={getStatusIcon(execution.status)}
                                                        label={execution.status}
                                                        size="small"
                                                        color={getStatusColor(execution.status)}
                                                    />
                                                </TableCell>
                                                <TableCell>{execution.details.applied}</TableCell>
                                                <TableCell>{execution.details.errors}</TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )
                    ) : (
                        <Alert severity="info">
                            {translate('cronMonitor.messages.noExecutionHistory')}
                        </Alert>
                    )}
                </CardContent>
            </Card>
        </Box>
    );
};
